import React, { useContext, useMemo } from 'react';
import { IDirectorate, EntityValidations, Directorate, ISpecificEntityFormProps, IUser, CustomField, Contributor } from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from '../cr/CrDropdown';
import { CrUserPicker } from '../cr/CrUserPicker';
import { CrTextField } from '../cr/CrTextField';
import { EntityForm } from '../EntityForm';
import { CrReportingCyclePicker } from '../cr/CrReportingCyclePicker';
import { ReportingCycleService } from '../../services/ReportingCycleService';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { DataContext } from '../DataContext';
import { IntegrationContext } from '../IntegrationContext';
import { OrbUserContext } from '../OrbUserContext';

export class DirectorateValidations extends EntityValidations {
    public GroupID: string = null;
    public ReportingCycle: string = null;
}

export const DirectorateForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { userPermissions, orbConfig } = useContext(OrbUserContext);
    const { disableDirectorateManagement } = useContext(IntegrationContext);
    const { dataServices, lookupData, loadLookupData: { customFieldDfns, entityStatuses, groups, userDirectorates, users: { all: allUsers } } } = useContext(DataContext);

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => groups(), [groups]);
    useMemo(() => userDirectorates(), [userDirectorates]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (directorate: IDirectorate): Promise<DirectorateValidations> => {
        const errors = new DirectorateValidations();

        if (directorate.Title === null || directorate.Title === '') {
            errors.Title = `${orbConfig.OrgL2Label} name is required`;
            errors.Valid = false;
        }
        else {
            errors.Title = null;
        }

        if (directorate.GroupID === null) {
            errors.GroupID = `${orbConfig.OrgL1Label} is required`;
            errors.Valid = false;
        }
        else {
            errors.GroupID = null;
        }

        if (!ReportingCycleService.reportingCycleIsValid(directorate)) {
            errors.ReportingCycle = 'Please select all values for the reporting cycle';
            errors.Valid = false;
        }
        else {
            errors.ReportingCycle = null;
        }

        return Promise.resolve(errors);
    };

    const directorateUsers = (directorate: IDirectorate): IUser[] => {
        if (lookupData?.Users?.All && directorate.ID) {
            return lookupData.Users.All.filter(u => LookupService.userIsInDirectorate(lookupData, u.ID, directorate.ID)
                || LookupService.userIsContributor(directorate, u.ID)
                || u.ID === directorate.DirectorUserID
                || u.ID === directorate.ReportApproverUserID
                || u.ID === directorate.ReportingLeadUserID
            );
        }
        return [];
    };

    return (
        <EntityForm<IDirectorate, DirectorateValidations>
            {...props}
            entityName={orbConfig.OrgL2Label}
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: directorate, ValidationErrors: errors } = formState;
                const mappedUsers = directorateUsers(directorate);
                const users = directorate.ID ? mappedUsers : lookupData.Users?.All;
                const suggestions = directorate.ID ? {
                    initialSuggestionsHeaderText: 'Directorate users',
                    initialSuggestions: mappedUsers,
                    noResultsFoundText: 'No users are mapped to this directorate'
                } : {};
                return (
                    <div>
                        <CrTextField
                            label="Name"
                            maxLength={255}
                            //required={true}
                            disabled={true}
                            className={styles.formField}
                            value={directorate.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />
                        <CrDropdown
                            label={orbConfig.OrgL1Label}
                            //required={true}
                            disabled={true}
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Groups)}
                            selectedKey={directorate.GroupID}
                            onChange={(_, option, index) => changeHandlers.changeDropdown(option, 'GroupID', index)}
                            errorMessage={errors.GroupID}
                        />
                        <CrUserPicker
                            label={orbConfig.OrgMgrL2Lable}
                            className={styles.formField}
                            users={users}
                            disabled={true}
                            selectedUsers={directorate.DirectorUserID && [directorate.DirectorUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'DirectorUserID')}
                            {...suggestions}
                        />
                        <CrReportingCyclePicker
                            label="Reports due:"
                            required={true}
                            className={styles.formField}
                            cycle={{ frequency: directorate.ReportingFrequency, dueDay: directorate.ReportingDueDay, startDate: directorate.ReportingStartDate }}
                            onChange={cycle => changeHandlers.changeReportingCycle(cycle)}
                            errorMessage={errors.ReportingCycle}
                        />
                        <CrUserPicker
                            label="Alternative report approver (ARA)"
                            className={styles.formField}
                            users={users}
                            selectedUsers={directorate.ReportApproverUserID && [directorate.ReportApproverUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'ReportApproverUserID')}
                            {...suggestions}
                        />
                        <CrUserPicker
                            label="Reporting lead (RL)"
                            className={styles.formField}
                            users={users}
                            selectedUsers={directorate.ReportingLeadUserID && [directorate.ReportingLeadUserID]}
                            onChange={u => changeHandlers.changeUserPicker(u, 'ReportingLeadUserID')}
                            {...suggestions}
                        />
                        <CrUserPicker
                            label="Headline update contributors (HUC)"
                            className={styles.formField}
                            users={users}
                            itemLimit={3}
                            selectedUsers={directorate.Contributors?.map(c => c.ContributorUserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                            {...suggestions}
                        />
                        <CrTextField
                            label="Objectives"
                            multiline
                            rows={6}
                            maxLength={10000}
                            className={styles.formField}
                            value={directorate.Objectives}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Objectives')}
                        />
                        <CrMultiDropdownWithText
                            label="CustomFields"
                            className={styles.formField}
                            textMaxLength={255}
                            options={lookupData.CustomFieldDfns.map(a => ({ key: a.ID, text: a.Title, textRequired: false }))}
                            selectedItems={LookupService.customFieldsToDropdownWithText(directorate.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />
                        {/* <CrDropdown
                            label="Status"
                            className={styles.formField}
                            disabled={disableDirectorateManagement}
                            options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                            selectedKey={directorate.EntityStatusID}
                            onChange={(_, o) => changeHandlers.changeStatusDropdown(o, 'EntityStatusID')}
                        /> */}
                    </div>
                );
            }}
            loadEntity={directorateId => dataServices.directorateService.read(directorateId, true, true)}
            loadNewEntity={() => new Directorate()}
            loadEntityValidations={() => new DirectorateValidations()}
            onValidateEntity={validateEntity}
            onCreate={directorate => dataServices.directorateService.create(directorate)}
            onUpdate={directorate => dataServices.directorateService.update(directorate.ID, directorate)}
            parentEntities={dataServices.directorateService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'Contributors',
                    ParentIdProperty: 'DirectorateID',
                    ChildIdProperty: 'ContributorUserID',
                    ChildService: dataServices.contributorService
                },
                {
                    ObjectParentProperty: 'CustomFields',
                    ParentIdProperty: 'DirectorateID',
                    ChildIdProperty: 'CustomFieldDefnID',
                    ChildService: dataServices.customFieldService
                }
            ]}
        />
    );
};
